import React, { useState, useEffect } from 'react';
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { ShieldCheck, Leaf, Hammer, Phone, Mail, MapPin, CheckCircle2, Truck, Droplets, Mountain } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from 'axios';
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import AboutUs from "./pages/AboutUs";
import Gallery from "./pages/Gallery";
import Admin from "./pages/Admin";
import ContactUs from "./pages/ContactUs";
import ServiceDetail from "./pages/ServiceDetail";
import ServiceArea from "./pages/ServiceArea";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { servicesData } from './data/services';
import QuoteForm from './components/QuoteForm';
import { initAttribution } from './utils/attribution';

const IMAGES = {
  hero: "https://images.pexels.com/photos/1022936/pexels-photo-1022936.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  hardscape: "https://images.unsplash.com/photo-1761637823407-ef47925c2714?q=80&w=2070&auto=format&fit=crop",
  residential: "https://images.pexels.com/photos/7031408/pexels-photo-7031408.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  worker: "https://images.unsplash.com/photo-1764173040050-b7a87824bc22?q=80&w=2070&auto=format&fit=crop"
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>
      <div className="container relative z-10 px-4 md:px-6 text-center">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white mb-6 drop-shadow-md">
          {t.hero.title_start} <span className="text-red-600">{t.hero.title_end}</span>
        </h1>
        <p className="mx-auto max-w-[700px] text-lg md:text-xl text-neutral-300 mb-8 drop-shadow-sm">
          {t.hero.subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 h-auto border-none" onClick={() => document.getElementById('quote').scrollIntoView({ behavior: 'smooth' })}>
            {t.hero.cta_primary}
          </Button>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useLanguage();
  
  // Use services from data file
  const residentialServices = servicesData.filter(s => s.category === 'residential');
  const commercialServices = servicesData.filter(s => s.category === 'commercial');

  return (
    <section id="services" className="py-20 bg-neutral-950">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">{t.services.title}</h2>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>
        
        <Tabs defaultValue="residential" className="w-full max-w-6xl mx-auto">
          <TabsList className="grid w-full grid-cols-2 mb-8 h-auto p-1 bg-neutral-900 border border-neutral-800 rounded-xl">
            <TabsTrigger value="residential" className="py-3 text-lg text-neutral-400 data-[state=active]:bg-neutral-800 data-[state=active]:text-red-500 rounded-lg transition-all">{t.services.tab_residential}</TabsTrigger>
            <TabsTrigger value="commercial" className="py-3 text-lg text-neutral-400 data-[state=active]:bg-neutral-800 data-[state=active]:text-red-500 rounded-lg transition-all">{t.services.tab_commercial}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="residential" className="space-y-8 animate-in fade-in-50 duration-500 slide-in-from-bottom-2">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {residentialServices.map((service) => (
                <Link to={`/services/${service.category}/${service.slug}`} key={service.id} className="block group">
                  <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-xl hover:border-red-900/50 transition-all h-full hover:bg-neutral-900/80">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-950/30 border border-red-900/50 rounded-lg h-fit group-hover:bg-red-900/20 transition-colors">
                        <service.icon className="h-6 w-6 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-500 transition-colors">{t.services[`${service.translationKey}_title`]}</h3>
                        <p className="text-neutral-400 text-sm">{t.services[`${service.translationKey}_desc`]}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="commercial" className="space-y-8 animate-in fade-in-50 duration-500 slide-in-from-bottom-2">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl shadow-black/50 group order-2 md:order-1 border border-neutral-800">
                <img 
                  src={IMAGES.worker} 
                  alt="Commercial Landscaping" 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="space-y-6 order-1 md:order-2">
                <div className="grid gap-4">
                  {commercialServices.map((service) => (
                    <Link to={`/services/${service.category}/${service.slug}`} key={service.id} className="block group">
                      <div className="flex gap-4 p-4 rounded-xl hover:bg-neutral-900/50 transition-colors border border-transparent hover:border-neutral-800">
                        <div className="p-3 bg-red-950/30 border border-red-900/50 rounded-lg h-fit">
                          <service.icon className="h-6 w-6 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-500 transition-colors">{t.services[`${service.translationKey}_title`]}</h3>
                          <p className="text-neutral-400">{t.services[`${service.translationKey}_desc`]}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const Features = () => {
  const { t } = useLanguage();
  const features = [
    t.features.licensed,
    t.features.expert,
    t.features.quality,
    t.features.ontime
  ];

  return (
    <section id="about" className="py-20 bg-black">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((f) => (
            <Card key={f.title} className="border border-neutral-800 shadow-lg hover:shadow-red-900/20 hover:border-red-900/50 transition-all bg-neutral-900/50">
              <CardHeader>
                <CheckCircle2 className="h-10 w-10 text-red-600 mb-2" />
                <CardTitle className="text-xl text-white">{f.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-400">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-black text-neutral-400 py-12 border-t border-neutral-800">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_71ec9cde-94ef-481f-b20e-e9fde84e406a/artifacts/ao9uzq47_6.png" 
                alt="PAC Logo" 
                className="h-24 w-auto" 
              />
            </div>
            <p className="max-w-xs">
              {t.footer.desc}
            </p>
          </div>
          <div>
            <h3 className="text-white font-bold mb-4">{t.footer.services_title}</h3>
            <ul className="space-y-2">
              <li><Link to="/services/residential/pave-uni-pavers" className="hover:text-red-500 transition-colors">{t.footer.residential}</Link></li>
              <li><Link to="/services/residential/asphalt-paving" className="hover:text-red-500 transition-colors">{t.footer.commercial}</Link></li>
              <li><Link to="/services/residential/retaining-walls" className="hover:text-red-500 transition-colors">{t.footer.hardscaping}</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-4">{t.footer.area_title}</h3>
            <p className="mb-4">{t.footer.area_desc}</p>
            <ul className="space-y-1">
              <li><Link to="/areas/west-island" className="hover:text-red-500 transition-colors">{t.footer.west_island}</Link></li>
              <li><Link to="/areas/tmr" className="hover:text-red-500 transition-colors">{t.footer.tmr}</Link></li>
              <li><Link to="/areas/plateau-rosemont" className="hover:text-red-500 transition-colors">{t.footer.plateau}</Link></li>
              <li><Link to="/areas/south-shore-laval" className="hover:text-red-500 transition-colors">{t.footer.south_shore}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-neutral-800 pt-8 text-center text-sm">
          <p>© {new Date().getFullYear()} {t.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const Home = () => (
  <>
    <Hero />
    <Services />
    <Features />
    <QuoteForm />
  </>
);

export default function App() {
  useEffect(() => {
    initAttribution();
  }, []);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-neutral-950 font-sans text-white selection:bg-red-900 selection:text-white">
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/services/:category/:slug" element={<ServiceDetail />} />
            <Route path="/areas/:slug" element={<ServiceArea />} />
          </Routes>
          <Footer />
          <Toaster position="top-center" theme="dark" />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}
